/**
 * Interactive spotlight tour.
 *
 * Design contract:
 *   * A step is `{ id, route, targetTestId, body, gesture? }`. `id` is stable
 *     — completed steps are stored server-side so re-entry skips them.
 *   * Backdrop dims the page to ~25% visibility. A single element is
 *     highlighted with a bright ring; a tooltip is anchored to it.
 *   * `gesture` — when set — renders a looping animated demo inside the
 *     tooltip instead of a static screenshot. Currently `commitment-drag` is
 *     the only gesture but the switch is easy to extend.
 *   * Navigation between screens is handled with react-router.
 *   * User controls: Next (advances + records step), Skip step (advances
 *     without recording), Exit tour (marks complete, closes for the session).
 *
 *  API used:
 *    GET  /api/tour/guided                  → { started_at, completed_at, steps_done[] }
 *    POST /api/tour/guided/start            → set started_at once
 *    POST /api/tour/guided/complete-step    body: { step_id }
 *    POST /api/tour/guided/complete         → set completed_at (Skip All / Finish)
 *    POST /api/tour/guided/reset            → restart from step 1 (Help menu)
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { X, ArrowRight, SkipForward, MousePointerClick } from 'lucide-react';
import { STEPS } from './steps';
import { GestureDemo } from './GestureDemo';

const GuidedTourContext = createContext(null);
export const useGuidedTour = () => useContext(GuidedTourContext);

const AUTO_START_WINDOW_HOURS = 24; // freshly-signed-up users get auto-triggered

export function GuidedTourProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepsDone, setStepsDone] = useState(new Set());
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const startedRef = useRef(false); // in-session guard against double start
  const autoTimerRef = useRef(null); // cancellable auto-trigger timer

  // Filter steps by role (some steps are Owner-only).
  const steps = useMemo(
    () => STEPS.filter(s => !s.roles || (user && s.roles.includes(user.role))),
    [user],
  );
  // Skip already-completed steps on entry.
  const remainingIndices = useMemo(
    () => steps.map((s, i) => stepsDone.has(s.id) ? -1 : i).filter(i => i >= 0),
    [steps, stepsDone],
  );
  const step = active && remainingIndices.length > 0 ? steps[remainingIndices[stepIndex] ?? remainingIndices[0]] : null;

  // ---- initial load: fetch progress + optional autotrigger ----
  useEffect(() => {
    if (!user) return;
    // Don't fire the tour (or even peek at /tour/guided) while the user is
    // in a forced-password-change flow — the tour's auto-navigate to /home
    // would clobber the /change-password redirect.
    if (user.force_password_change) return;
    let alive = true;
    api.get('/tour/guided').then(async (r) => {
      if (!alive) return;
      const done = new Set(r.data.steps_done || []);
      setStepsDone(done);
      // Autotrigger for freshly-created accounts that haven't seen the tour yet.
      const createdAt = user.created_at ? new Date(user.created_at).getTime() : 0;
      const ageHours = createdAt ? (Date.now() - createdAt) / 3.6e6 : Infinity;
      const shouldAuto =
        !startedRef.current
        && ageHours < AUTO_START_WINDOW_HOURS
        && !r.data.started_at
        && !r.data.completed_at;
      if (shouldAuto) {
        startedRef.current = true;
        // Delay a beat so the Home page mounts first. Store the timer so it
        // can be cancelled if the user takes any action in the meantime.
        autoTimerRef.current = setTimeout(() => {
          autoTimerRef.current = null;
          // Re-check before firing: user may have already started, exited,
          // or completed the tour during the delay.
          if (!alive) return;
          start('auto');
        }, 900);
      }
    }).catch(() => {});
    return () => {
      alive = false;
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
        autoTimerRef.current = null;
      }
    };
  }, [user?.id]);

  // ---- when active, resolve the current target element + navigate if needed ----
  useEffect(() => {
    if (!active || !step) { setRect(null); setReady(false); return; }
    // Never let the tour hijack /change-password (the forced-change flow
    // must complete before anything else). Bail out and close the tour.
    if (user?.force_password_change || location.pathname === '/change-password') {
      setActive(false);
      return;
    }
    let disposed = false;
    // Navigate to the step's route if we're not already there.
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    // Wait for the target element to mount, then measure it.
    let poll = null;
    const findAndMeasure = () => {
      if (disposed) return;
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Ignore zero-height elements (mounted but not painted yet).
      if (r.width === 0 || r.height === 0) return;
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      setReady(true);
      // Best-effort scroll into view.
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      if (poll) clearInterval(poll);
    };
    setReady(false);
    setRect(null);
    findAndMeasure();
    poll = setInterval(findAndMeasure, 200);
    const timeout = setTimeout(() => { if (poll) clearInterval(poll); }, 6000); // give up after 6s
    // Also reposition on resize + scroll.
    const onWinChange = () => {
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener('resize', onWinChange);
    window.addEventListener('scroll', onWinChange, true);
    return () => {
      disposed = true;
      if (poll) clearInterval(poll);
      clearTimeout(timeout);
      window.removeEventListener('resize', onWinChange);
      window.removeEventListener('scroll', onWinChange, true);
    };
  }, [active, step, location.pathname, navigate, user?.force_password_change]);

  // ---- Controls ----
  const start = useCallback(async (reason = 'manual') => {
    // Cancel any pending auto-trigger — the user chose start explicitly.
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    try { await api.post('/tour/guided/start'); } catch { /* soft-fail */ }
    setStepIndex(0);
    setActive(true);
    void reason; // reserved for analytics
  }, []);

  const exit = useCallback(async (finish = false) => {
    // Cancel any pending auto-trigger so we don't reopen after exit.
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    setActive(false);
    if (finish) {
      try { await api.post('/tour/guided/complete'); } catch { /* soft-fail */ }
    }
  }, []);

  const advance = useCallback(async ({ record }) => {
    const cur = step;
    if (!cur) return;
    if (record) {
      try { await api.post('/tour/guided/complete-step', { step_id: cur.id }); } catch { /* soft-fail */ }
      // When recording, stepsDone grows and remainingIndices naturally shifts
      // so the next remaining step slides into stepIndex 0 — don't also bump.
      setStepsDone(prev => {
        const next = new Set([...prev, cur.id]);
        // If nothing remains, finish the tour.
        const remaining = steps.filter(s => !next.has(s.id)).length;
        if (remaining === 0) exit(true);
        return next;
      });
      setStepIndex(0);
    } else {
      // Skip without recording — advance past the current position.
      setStepIndex(prev => {
        const rem = steps.filter(s => !stepsDone.has(s.id)).length;
        const next = prev + 1;
        if (next >= rem) { exit(true); return 0; }
        return next;
      });
    }
  }, [step, steps, stepsDone, exit]);

  const restart = useCallback(async () => {
    try { await api.post('/tour/guided/reset'); } catch { /* soft-fail */ }
    setStepsDone(new Set());
    setStepIndex(0);
    setActive(true);
  }, []);

  const value = useMemo(() => ({ active, start, exit, restart, stepsDone, totalSteps: steps.length }),
    [active, start, exit, restart, stepsDone, steps.length]);

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
      {active && step && (
        <TourOverlayPortal
          step={step}
          rect={rect} ready={ready}
          onNext={() => advance({ record: true })}
          onSkip={() => advance({ record: false })}
          onExit={() => exit(true)}
          index={remainingIndices[stepIndex] ?? 0}
          total={steps.length}
          doneCount={stepsDone.size}
        />
      )}
    </GuidedTourContext.Provider>
  );
}

/** Full-screen backdrop + spotlight cutout + anchored tooltip. */
function TourOverlayPortal({ step, rect, ready, onNext, onSkip, onExit, total, doneCount }) {
  const pad = 8;
  const spotlight = ready && rect ? {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  } : null;

  // Tooltip placement: prefer below the spotlight; flip above if there's not
  // enough space; horizontally clamp to viewport.
  const tooltip = useMemo(() => {
    if (!spotlight) return { top: 80, left: 24, width: 320, side: 'bottom' };
    const W = window.innerWidth, H = window.innerHeight;
    const width = Math.min(360, W - 32);
    const spaceBelow = H - (spotlight.top + spotlight.height) - 16;
    const spaceAbove = spotlight.top - 16;
    const side = spaceBelow >= 220 || spaceBelow > spaceAbove ? 'bottom' : 'top';
    const top = side === 'bottom'
      ? spotlight.top + spotlight.height + 12
      : Math.max(16, spotlight.top - 220);
    const centerX = spotlight.left + spotlight.width / 2;
    let left = centerX - width / 2;
    left = Math.max(16, Math.min(left, W - width - 16));
    return { top, left, width, side };
  }, [spotlight]);

  return (
    <div className="fixed inset-0 z-[100] pointer-events-auto" data-testid="guided-tour-overlay">
      {/* Dimmer — 4 rectangles around the spotlight so pointer events stay off the target. */}
      {spotlight ? (
        <>
          <div className="absolute bg-black/75" style={{ top: 0, left: 0, right: 0, height: spotlight.top }} />
          <div className="absolute bg-black/75" style={{ top: spotlight.top + spotlight.height, left: 0, right: 0, bottom: 0 }} />
          <div className="absolute bg-black/75" style={{ top: spotlight.top, left: 0, width: spotlight.left, height: spotlight.height }} />
          <div className="absolute bg-black/75" style={{ top: spotlight.top, left: spotlight.left + spotlight.width, right: 0, height: spotlight.height }} />
          {/* Highlight ring around the target */}
          <div
            aria-hidden="true"
            className="absolute rounded-md ring-4 ring-[hsl(var(--primary))] shadow-[0_0_0_9999px_transparent]"
            style={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height,
                     transition: 'top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease' }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/75" />
      )}

      {/* Tooltip */}
      <div
        role="dialog"
        aria-label={`Guided tour · ${step.title}`}
        data-testid={`guided-tour-tooltip-${step.id}`}
        className="absolute rounded-xl bg-[hsl(var(--surface-1))] border border-[hsl(var(--primary)/.35)] shadow-2xl"
        style={{ top: tooltip.top, left: tooltip.left, width: tooltip.width, transition: 'top 220ms ease, left 220ms ease' }}
      >
        <div className="px-3.5 py-2 flex items-center gap-2 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <MousePointerClick size={14} className="text-[hsl(var(--primary))]" />
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--primary))] flex-1 truncate">
            {step.title}
          </div>
          <span className="text-[10.5px] text-[hsl(var(--foreground-muted))]">{doneCount + 1} / {total}</span>
          <button
            onClick={onExit}
            aria-label="Exit tour"
            data-testid="guided-tour-exit"
            className="w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--surface-2))]"
          ><X size={12} /></button>
        </div>
        <div className="p-3.5 space-y-3">
          {step.gesture && <GestureDemo kind={step.gesture} />}
          <div className="text-[15px] leading-snug">{step.body}</div>
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={onSkip}
              data-testid="guided-tour-skip-step"
              className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--foreground-muted))] underline underline-offset-2 hover:text-[hsl(var(--foreground))]"
            >
              <SkipForward size={11} />Skip this step
            </button>
            <button
              onClick={onNext}
              data-testid="guided-tour-next"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-semibold
                         bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
            >
              Next <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
